import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from "react";
import {
  Notification,
  useGetNotificationsListFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { IResponseError } from "shared/request/IResponseError";
import { AxiosError } from "axios";

export type NotificationsContextType = {
  refreshNotifications: VoidFunction;
  notificationsList: Notification[];
  notificationsErrors?: AxiosError<IResponseError>;
  hasNewNotifications?: boolean;
}

export const NotificationsContext = createContext<NotificationsContextType | undefined>(
  undefined
);

export const useNotificationsContext = (): NotificationsContextType => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotificationsContext must be used within NotificationsContextProvider"
    );
  }
  return ctx;
};

export const NotificationsContextProvider = ({
  children,
}: PropsWithChildren) => {
  const clientCode = ClientInfoState.getCode();

  const {
    data: notificationsList = [],
    mutate: refreshAllNotifications,
    error: notificationsErrors,
  } = useGetNotificationsListFunctionRequest({
    xClientCode: clientCode,
  });

  const {
    data: newNotificationsList = [],
    error: newNotificationsErrors,
    mutate: refreshNewNotifications
  } = useGetNotificationsListFunctionRequest({
    xClientCode: clientCode,
    archived: false,
  });

  const refreshNotifications = useCallback(() => {
    refreshAllNotifications();
    refreshNewNotifications();
  }, [refreshAllNotifications, refreshNewNotifications])

  const contextValue = useMemo<NotificationsContextType>(
    () => ({
      refreshNotifications,
      notificationsList,
      notificationsErrors,
      hasNewNotifications: !newNotificationsErrors && newNotificationsList && newNotificationsList.length > 0,
    }),
    [
      refreshNotifications,
      notificationsList,
      notificationsErrors,
      newNotificationsErrors,
      newNotificationsList
    ]
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
    </NotificationsContext.Provider>
  );
};
